import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Globe, MailCheck, Images, Armchair, QrCode, CalendarClock,
  ArrowRight, Check, Quote, Menu,
} from "lucide-react";
import { useState } from "react";
import TableFinder from "@/components/public/TableFinder";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut", delay: i * 0.08 } }),
};

const HERO_IMG = "https://images.pexels.com/photos/5705865/pexels-photo-5705865.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const FEATURE_IMG = "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHw0fHx3ZWRkaW5nJTIwdGFibGUlMjBzZXR1cHxlbnwwfHx8fDE3ODg1MDUxNTB8MA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: Globe, title: "Pagina pubblica", desc: "Un sito elegante per i vostri invitati, con nomi, countdown e la vostra storia." },
  { icon: MailCheck, title: "RSVP online", desc: "Raccogli conferme, ospiti extra e note alimentari senza fogli di calcolo." },
  { icon: Images, title: "Galleria condivisa", desc: "Gli invitati caricano e firmano le foto del grande giorno, senza registrarsi." },
  { icon: Armchair, title: "Piano tavoli visuale", desc: "Disegna la sala trascinando i tavoli e assegnando ogni ospite al suo posto." },
  { icon: QrCode, title: "QR code inviti", desc: "Un codice da stampare sugli inviti che porta dritto alla pagina evento." },
  { icon: CalendarClock, title: "Programma della giornata", desc: "Racconta ogni momento, dalla cerimonia alla festa, in una timeline curata." },
];

const STEPS = [
  { n: "01", title: "Crea il tuo evento", desc: "Registrati e imposta nomi, data e tema in pochi click." },
  { n: "02", title: "Personalizza tutto", desc: "Storia, programma, colori e piano tavoli, come li immagini." },
  { n: "03", title: "Condividi con gli ospiti", desc: "Invia il link o il QR code e raccogli RSVP e foto." },
];

const FAQS = [
  { q: "È davvero gratis per iniziare?", a: "Sì. Puoi creare il tuo evento, la pagina pubblica e raccogliere RSVP senza costi. Il piano Pro sblocca funzioni avanzate." },
  { q: "Gli invitati devono registrarsi?", a: "No. Accedono alla pagina tramite un link o QR code e possono confermare la presenza e caricare foto liberamente." },
  { q: "Posso gestire più matrimoni?", a: "Certo. Dallo stesso account puoi creare e passare tra più eventi in qualsiasi momento." },
  { q: "I miei dati sono al sicuro?", a: "Ogni evento è isolato e accessibile solo a te. L'accesso è protetto e le foto restano private finché non le condividi." },
];

function Nav() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-[#e3ddd2]">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="landing-brand">
          <img src="/brand/logo-black.png" alt="ourweddly" className="h-10 sm:h-12 w-auto" />
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm text-neutral-600">
          <a href="#funzionalita" className="hover:text-[#1a1a1a] transition-colors">Funzionalità</a>
          <a href="#come-funziona" className="hover:text-[#1a1a1a] transition-colors">Come funziona</a>
          <a href="#prezzi" className="hover:text-[#1a1a1a] transition-colors">Prezzi</a>
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <Link to="/login" className="text-sm text-neutral-600 hover:text-[#1a1a1a] transition-colors px-3" data-testid="nav-login-link">Accedi</Link>
          <Link to="/register" className="btn-wed !py-2 !px-5 text-sm" data-testid="nav-register-link">Inizia gratis</Link>
        </div>
        <button className="md:hidden p-2" onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle"><Menu size={22} /></button>
      </div>
      {open && (
        <div className="md:hidden border-t border-[#e3ddd2] bg-white px-6 py-4 flex flex-col gap-3 text-sm">
          <a href="#funzionalita" onClick={() => setOpen(false)}>Funzionalità</a>
          <a href="#come-funziona" onClick={() => setOpen(false)}>Come funziona</a>
          <a href="#prezzi" onClick={() => setOpen(false)}>Prezzi</a>
          <Link to="/login" data-testid="nav-login-link-mobile">Accedi</Link>
          <Link to="/register" className="btn-wed !py-2 text-center" data-testid="nav-register-link-mobile">Inizia gratis</Link>
        </div>
      )}
    </header>
  );
}

export default function Landing() {
  return (
    <div className="grain min-h-screen" data-testid="landing-page">
      <Nav />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 sm:px-8 pt-16 sm:pt-24 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div variants={fadeUp} initial="hidden" animate="show">
            <p className="eyebrow mb-5">Wedding management</p>
            <h1 className="font-wed text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Il vostro matrimonio,<br />in un unico posto elegante.
            </h1>
            <p className="text-neutral-600 text-lg mt-6 max-w-md leading-relaxed">
              Crea una pagina raffinata per i tuoi invitati, raccogli le conferme, disegna il piano tavoli e condividi le foto. Tutto da un'unica dashboard.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-9">
              <Link to="/register" className="btn-wed flex items-center gap-2" data-testid="hero-cta-register">
                Inizia gratis <ArrowRight size={18} />
              </Link>
              <Link to="/marco-e-giulia" className="btn-outline-wed" data-testid="hero-cta-demo">Guarda un esempio</Link>
            </div>
            <p className="text-xs text-neutral-400 mt-4">Nessuna carta di credito richiesta.</p>
          </motion.div>

          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={1} className="relative">
            <div className="rounded-3xl overflow-hidden border border-[#e3ddd2] shadow-md">
              <img src={HERO_IMG} alt="Coppia di sposi in una villa italiana" className="w-full h-[420px] sm:h-[520px] object-cover" fetchpriority="high" />
            </div>
            <div className="absolute -bottom-6 -left-4 sm:-left-6 bg-white border border-[#e3ddd2] rounded-2xl px-5 py-4 shadow-sm">
              <p className="text-xs text-neutral-400">Conferme ricevute</p>
              <p className="font-wed text-2xl" style={{ color: "var(--wed-primary)" }}>128 invitati</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Interactive demo -- real data from the live example wedding, so a
          visitor can feel the guest experience instead of reading about it. */}
      <section className="bg-[#f5f3ec] border-y border-[#e3ddd2]">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 pt-20 sm:pt-24 text-center">
          <p className="eyebrow mb-4">Prova tu stesso</p>
          <h2 className="font-wed text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-4">Così lo vivono i tuoi invitati</h2>
          <p className="text-neutral-600 max-w-xl mx-auto">
            Questa è la pagina reale del matrimonio demo di Marco & Giulia, con invitati e tavoli già assegnati. Cerca un nome e guarda cosa vede chi riceve il tuo invito.
          </p>
        </div>
        <TableFinder
          slug="marco-e-giulia"
          title="Dove sono seduto?"
          subtitle="Scrivi un nome di prova qui sotto."
          suggestions={["Anna Rossi", "Elena Costa"]}
        />
        <div className="text-center pb-16 sm:pb-20 -mt-6">
          <Link to="/marco-e-giulia" className="text-sm font-medium inline-flex items-center gap-1.5" style={{ color: "var(--wed-primary)" }} data-testid="demo-full-page-link">
            Guarda la pagina demo completa <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* Features */}
      <section id="funzionalita" className="max-w-6xl mx-auto px-6 sm:px-8 py-20 sm:py-28">
        <div className="max-w-2xl mb-14">
          <p className="eyebrow mb-4">Tutto l'essenziale</p>
          <h2 className="font-wed text-3xl sm:text-4xl lg:text-5xl tracking-tight">Ogni dettaglio del grande giorno, curato</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} custom={i % 3}
              className="bg-white border border-[#e3ddd2] rounded-3xl p-7 hover:-translate-y-1 hover:shadow-md transition-[transform,box-shadow] duration-300"
              data-testid={`feature-card-${i}`}
            >
              <div className="w-11 h-11 rounded-full flex items-center justify-center mb-5" style={{ background: "color-mix(in srgb, var(--wed-primary) 15%, white)" }}>
                <f.icon size={20} strokeWidth={1.5} style={{ color: "var(--wed-primary)" }} />
              </div>
              <h3 className="font-wed text-xl mb-2">{f.title}</h3>
              <p className="text-sm text-neutral-500 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="come-funziona" className="bg-[#f5f3ec] border-y border-[#e3ddd2]">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-20 sm:py-28">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="rounded-3xl overflow-hidden border border-[#e3ddd2]">
              <img src={FEATURE_IMG} alt="Allestimento tavoli matrimonio" className="w-full h-[360px] sm:h-[460px] object-cover" />
            </div>
            <div>
              <p className="eyebrow mb-4">Come funziona</p>
              <h2 className="font-wed text-3xl sm:text-4xl tracking-tight mb-10">Dalla registrazione alla festa, in tre passi</h2>
              <div className="space-y-8">
                {STEPS.map((s, i) => (
                  <motion.div key={s.n} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} className="flex gap-5">
                    <span className="font-wed text-2xl w-12 shrink-0" style={{ color: "var(--wed-primary)" }}>{s.n}</span>
                    <div>
                      <h3 className="font-wed text-xl mb-1">{s.title}</h3>
                      <p className="text-sm text-neutral-500 leading-relaxed">{s.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social proof */}
      <section className="max-w-4xl mx-auto px-6 sm:px-8 py-24 sm:py-32 text-center">
        <Quote size={40} strokeWidth={1.2} className="mx-auto mb-8" style={{ color: "var(--wed-primary)" }} />
        <blockquote className="font-wed text-2xl sm:text-3xl lg:text-4xl leading-snug italic tracking-tight">
          “Abbiamo organizzato tutto il matrimonio da qui. Gli invitati hanno adorato la pagina e le foto condivise sono un ricordo bellissimo.”
        </blockquote>
        <p className="text-sm text-neutral-500 mt-8">Marco & Giulia — Toscana, 2026</p>
      </section>

      {/* Pricing */}
      <section id="prezzi" className="max-w-5xl mx-auto px-6 sm:px-8 py-20 sm:py-28">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="eyebrow mb-4">Prezzi</p>
          <h2 className="font-wed text-3xl sm:text-4xl lg:text-5xl tracking-tight">Semplice e trasparente</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
          <div className="bg-white border border-[#e3ddd2] rounded-3xl p-8" data-testid="pricing-free">
            <h3 className="font-wed text-2xl mb-1">Gratis</h3>
            <p className="text-sm text-neutral-500 mb-6">Per iniziare a organizzare</p>
            <p className="font-wed text-4xl mb-8">€0</p>
            <ul className="space-y-3 text-sm text-neutral-600 mb-8">
              {["Pagina pubblica", "RSVP online", "Galleria foto", "1 evento"].map((t) => (
                <li key={t} className="flex items-center gap-3"><Check size={16} style={{ color: "var(--wed-primary)" }} /> {t}</li>
              ))}
            </ul>
            <Link to="/register" className="btn-outline-wed w-full block text-center" data-testid="pricing-free-cta">Inizia gratis</Link>
          </div>
          <div className="rounded-3xl p-8 text-white relative overflow-hidden" style={{ background: "#1a1a1a" }} data-testid="pricing-pro">
            <span className="absolute top-6 right-6 text-[0.65rem] tracking-[0.2em] uppercase px-3 py-1 rounded-full" style={{ background: "var(--wed-primary)" }}>Consigliato</span>
            <h3 className="font-wed text-2xl mb-1">Pro</h3>
            <p className="text-sm text-white/60 mb-6">Per il matrimonio dei sogni</p>
            <p className="font-wed text-4xl mb-8">€49<span className="text-lg text-white/50"> / evento</span></p>
            <ul className="space-y-3 text-sm text-white/80 mb-8">
              {["Tutto del piano Gratis", "Piano tavoli visuale", "QR code inviti", "Eventi illimitati", "Supporto prioritario"].map((t) => (
                <li key={t} className="flex items-center gap-3"><Check size={16} style={{ color: "var(--wed-primary)" }} /> {t}</li>
              ))}
            </ul>
            <Link to="/register" className="w-full block text-center rounded-full py-3 text-sm font-medium" style={{ background: "var(--wed-primary)" }} data-testid="pricing-pro-cta">Prova il Pro</Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 sm:px-8 py-20 sm:py-28">
        <div className="text-center mb-12">
          <p className="eyebrow mb-4">Domande frequenti</p>
          <h2 className="font-wed text-3xl sm:text-4xl tracking-tight">Le risposte che cerchi</h2>
        </div>
        <div className="divide-y divide-[#e3ddd2] border-t border-b border-[#e3ddd2]">
          {FAQS.map((f, i) => (
            <details key={i} className="group py-5" data-testid={`faq-${i}`}>
              <summary className="flex items-center justify-between cursor-pointer list-none font-medium text-[#1a1a1a]">
                {f.q}
                <span className="text-neutral-400 group-open:rotate-45 transition-transform duration-200 text-xl leading-none">+</span>
              </summary>
              <p className="text-sm text-neutral-500 mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA + Footer */}
      <section className="bg-[#1a1a1a] text-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-24 text-center">
          <h2 className="font-wed text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-6">Pronti a dire "sì"?</h2>
          <p className="text-white/60 mb-9 max-w-lg mx-auto">Crea gratuitamente la pagina del tuo matrimonio e inizia a raccogliere le conferme oggi stesso.</p>
          <Link to="/register" className="inline-flex items-center gap-2 rounded-full py-3.5 px-8 text-sm font-medium" style={{ background: "var(--wed-primary)" }} data-testid="footer-cta-register">
            Inizia gratis <ArrowRight size={18} />
          </Link>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-6xl mx-auto px-6 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-white/50">
            <img src="/brand/logo-white.png" alt="ourweddly" className="h-8 w-auto" />
            <p>© {new Date().getFullYear()} ourweddly. Fatto con cura in Italia.</p>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
